import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { countWords } from "@/lib/emotions";
import { Cover } from "@/components/Cover";
import { useRemanso } from "@/context/RemansoContext";
import { EmotionWheel } from "@/components/EmotionWheel";
import { routeById } from "@/lib/routes";
import { useAuth } from "@/context/AuthContext";
import { putDraft, removeDraft } from "@/lib/drafts";

const SAVE_DELAY = 700;
const COVER_DELAY = 1200;
const ACTIVE_PAUSE_MS = 60000;
const GIRO_IDLE_MS = 8000;
const MAX_SAVE_WAIT_MS = 5000;
const STATUS = { saving: "Guardando…", saved: "Guardado" };

const DeleteEntry = ({ onConfirm }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button type="button" data-testid="delete-entry-button" onClick={() => (armed ? onConfirm() : setArmed(true))}
      className={`rounded-full px-2 py-1 transition-colors hover:bg-resaltado ${armed ? "text-tinta font-medium" : "text-meta"}`}>
      {armed ? "Toca otra vez para borrarla para siempre" : "Borrar entrada"}
    </button>
  );
};

const AutoTextarea = ({ value, inputRef, ...props }) => {
  const ref = inputRef;
  useLayoutEffect(() => {
    const el = ref.current;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} value={value} rows={6} {...props} />;
};

export const EntryEditor = ({ initial, saved, onSaved, onDeleted, onCompanion }) => {
  const remanso = useRemanso();
  const bodyRef = useRef(null);
  const [title, setTitle] = useState(initial.title);
  const [body, setBody] = useState(initial.body);
  const [moods, setMoods] = useState(initial.moods || []);
  const [route, setRoute] = useState(initial.route || "libre");
  const [status, setStatus] = useState(saved ? "saved" : "");
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(false);
  const failedRef = useRef(false);
  const words = countWords(body);
  const [coverWords, setCoverWords] = useState(words);

  const savedRef = useRef(saved);
  const deletedRef = useRef(false);
  const pendingRef = useRef(false);
  const queue = useRef(Promise.resolve());
  const latest = useRef({ title, body, moods, route });
  const writingMs = useRef(initial.writing_ms || 0);
  const lastKey = useRef(0);
  latest.current = { title, body, moods, route };

  const lastSaveAt = useRef(Date.now());
  const userId = useAuth().user?.user_id;
  // encrypted copy on this device, only while the server has not confirmed the text
  const persistDraft = () => {
    const { title: t, body: b } = latest.current;
    if (deletedRef.current || (!savedRef.current && !t.trim() && !b.trim())) return;
    putDraft(userId, {
      id: initial.id, saved: savedRef.current, ...latest.current,
      writing_ms: Math.round(writingMs.current), companion: savedRef.current ? undefined : remanso.getThread(),
    }).catch(() => {});
  };
  const save = () => {
    pendingRef.current = false;
    lastSaveAt.current = Date.now();
    queue.current = queue.current.then(async () => {
      const { title: t, body: b, moods: md, route: rt } = latest.current;
      if (deletedRef.current || (!savedRef.current && !t.trim() && !b.trim())) return;
      if (!navigator.onLine) { failedRef.current = true; setStatus(""); setOffline(true); persistDraft(); return; }
      setStatus("saving");
      const payload = { title: t, body: b, moods: md, route: rt, writing_ms: Math.round(writingMs.current) };
      try {
        const { data } = savedRef.current
          ? await api.put(`/entries/${initial.id}`, payload)
          : await api.post("/entries", { id: initial.id, companion: remanso.getThread(), ...payload });
        savedRef.current = true;
        failedRef.current = false;
        removeDraft(initial.id);
        setStatus("saved");
        setError("");
        setOffline(false);
        onSaved(data);
      } catch (e) {
        failedRef.current = true;
        persistDraft();
        setStatus("");
        if (!e.response && !navigator.onLine) setOffline(true);
        else setError("No pudimos guardar. Tu texto sigue aquí; lo intentamos de nuevo al seguir escribiendo.");
      }
    });
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!pendingRef.current) return undefined;
    if (failedRef.current || !navigator.onLine) persistDraft();
    const t = setTimeout(() => saveRef.current(), SAVE_DELAY);
    return () => clearTimeout(t);
  }, [title, body, moods, route]);

  useEffect(() => {
    const t = setTimeout(() => setCoverWords(words), COVER_DELAY);
    return () => clearTimeout(t);
  }, [words]);

  // connection lost mid-entry: keep the text on screen and save as soon as the network is back
  useEffect(() => {
    const down = () => setOffline(true);
    const up = () => {
      setOffline(false);
      if (failedRef.current || pendingRef.current) saveRef.current();
    };
    window.addEventListener("offline", down);
    window.addEventListener("online", up);
    return () => { window.removeEventListener("offline", down); window.removeEventListener("online", up); };
  }, []);

  // flush pending text when leaving the entry
  useEffect(() => () => { if (pendingRef.current) saveRef.current(); }, []);

  // tab hidden: normal save; page unloading: keepalive request so the last words survive
  useEffect(() => {
    const onHidden = () => { if (document.visibilityState === "hidden" && pendingRef.current) saveRef.current(); };
    const onPageHide = () => {
      if (!pendingRef.current || deletedRef.current) return;
      const { title: t, body: b, moods: md, route: rt } = latest.current;
      if (!savedRef.current && !t.trim() && !b.trim()) return;
      if (!navigator.onLine || failedRef.current) persistDraft();
      pendingRef.current = false;
      const payload = { title: t, body: b, moods: md, route: rt, writing_ms: Math.round(writingMs.current) };
      const base = `${process.env.REACT_APP_BACKEND_URL}/api/entries`;
      const sent = fetch(savedRef.current ? `${base}/${initial.id}` : base, {
        method: savedRef.current ? "PUT" : "POST", keepalive: true, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(savedRef.current ? payload : { id: initial.id, companion: remanso.getThread(), ...payload }),
      });
      // if the page survives (bfcache), later saves wait for this one and switch to PUT once it exists
      queue.current = queue.current.then(() => sent.then((r) => {
        if (r.ok || r.status === 409) { savedRef.current = true; setStatus("saved"); }
        else pendingRef.current = true;
      }, () => { pendingRef.current = true; }));
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { bind, typing, syncMoods } = remanso;
  useEffect(() => { syncMoods(moods); }, [moods, syncMoods]);
  const tone = useRef({ sinceCheck: 0, checking: false, giroDue: false });
  const idleTimer = useRef(null);
  useEffect(() => () => clearTimeout(idleTimer.current), []);

  const fireGiro = async () => {
    tone.current.giroDue = false;
    if (!(await remanso.ask("giro", { silent: true }))) tone.current.giroDue = true;
  };
  // silent tone monitor for the 15-minute rule
  const runTone = async () => {
    tone.current.checking = true;
    tone.current.sinceCheck = 0;
    try {
      const { data } = await api.post(`/entries/${initial.id}/tone`, { body: latest.current.body });
      const last = remanso.getThread().at(-1);
      if (data.riesgo) { if (last?.kind !== "care") remanso.ask("ask", { silent: true }); }
      else if (data.giro_due) {
        tone.current.giroDue = true;
        if (Date.now() - lastKey.current >= GIRO_IDLE_MS) fireGiro();
      }
    } catch { /* silent by design */ } finally {
      tone.current.checking = false;
    }
  };
  const onCompanionRef = useRef(onCompanion);
  onCompanionRef.current = onCompanion;
  useEffect(() => bind({
    id: initial.id,
    companion: initial.companion || [],
    route: initial.route || "libre",
    moods: initial.moods || [],
    setMoods: (next) => { pendingRef.current = true; setMoods(next); },
    after: initial.after,
    saveAfter: (value) => {
      queue.current = queue.current.then(() => savedRef.current && api.put(`/entries/${initial.id}`, { after: value }).catch(() => {}));
    },
    body: initial.body,
    title: initial.title || "",
    getDraft: () => latest.current,
    flush: async () => { if (pendingRef.current) saveRef.current(); await queue.current; },
    select: (quote) => {
      const el = bodyRef.current;
      const at = el?.value.indexOf(quote) ?? -1;
      if (at < 0) return;
      el.focus();
      el.setSelectionRange(at, at + quote.length);
    },
    onCompanion: (list) => onCompanionRef.current?.(initial.id, list),
    onRoute: (id) => { pendingRef.current = true; setRoute(id); },
    persistCompanion: (list) => {
      queue.current = queue.current.then(() => savedRef.current && api.put(`/entries/${initial.id}`, { companion: list })
        .catch(() => setError("No pudimos guardar. Tu texto sigue aquí; lo intentamos de nuevo al seguir escribiendo.")));
    },
  }), [bind, initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (setter, isBody) => (e) => {
    const now = Date.now();
    if (lastKey.current && now - lastKey.current < ACTIVE_PAUSE_MS) {
      writingMs.current += now - lastKey.current;
      tone.current.sinceCheck += now - lastKey.current;
    }
    lastKey.current = now;
    pendingRef.current = true;
    setter(e.target.value);
    typing(isBody ? e.target.value : undefined, isBody ? undefined : e.target.value);
    if (now - lastSaveAt.current > MAX_SAVE_WAIT_MS) saveRef.current();
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => { if (tone.current.giroDue) fireGiro(); }, GIRO_IDLE_MS);
    const cfg = remanso.config;
    if (cfg && savedRef.current && !tone.current.checking && tone.current.sinceCheck >= cfg.tone_interval_ms
      && countWords(latest.current.body) >= 40) runTone();
  };

  const remove = async () => {
    deletedRef.current = true;
    pendingRef.current = false;
    removeDraft(initial.id);
    await queue.current;
    try {
      if (savedRef.current) await api.delete(`/entries/${initial.id}`);
      onDeleted(initial.id);
    } catch {
      deletedRef.current = false;
      setError("No pudimos borrar la entrada. Intenta de nuevo en un momento.");
    }
  };

  const date = new Date(initial.created_at).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <section data-testid="screen-escribir"
      className="settle bg-hoja border border-linea rounded-[24px] overflow-hidden flex flex-col min-h-[70vh] lg:min-h-[calc(100vh-4rem)]">
      <Cover id={initial.id} words={coverWords} moods={moods} height={72} testId="entry-cover" />
      <div className="flex-1 flex flex-col px-5 pt-7 pb-5 sm:px-10 lg:px-14 lg:pt-10 lg:pb-8">
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow" data-testid="entry-date">{date}</p>
          <p className="text-[13px] text-meta shrink-0" aria-live="polite" data-testid="save-status">{STATUS[status] || ""}</p>
        </div>
        {offline
          ? <p className="calm-note mt-4" role="status" data-testid="offline-note">Sin conexión. Tu texto sigue aquí; lo guardamos en cuanto vuelva la red.</p>
          : error && <p className="calm-note mt-4" role="status" data-testid="save-error">{error}</p>}
        {route !== "libre" && <p className="eyebrow text-acento-texto mt-6 -mb-3" data-testid="entry-route-label">Ruta · {routeById(route).name}</p>}
        <input value={title} onChange={edit(setTitle)} placeholder="Sin título" aria-label="Título" data-testid="entry-title-input"
          className="mt-6 w-full max-w-[60ch] rounded-[8px] bg-transparent font-serif text-[28px] lg:text-[32px] leading-tight text-tinta placeholder:text-meta" />
        <AutoTextarea inputRef={bodyRef} value={body} onChange={edit(setBody, true)} placeholder="Escribe lo que traes." aria-label="Tu entrada" data-testid="entry-body-input"
          className="bare mt-5 flex-1 w-full max-w-[60ch] resize-none overflow-hidden bg-transparent font-serif text-[20px] lg:text-[26px] leading-[1.6] text-tinta placeholder:text-meta" />
        <EmotionWheel moods={moods} onChange={(next) => { pendingRef.current = true; setMoods(next); }} />
        <div className="mt-8 pt-4 border-t border-linea flex items-center justify-between gap-4 text-[13px]">
          <span className="text-meta" data-testid="word-count">{words} {words === 1 ? "palabra" : "palabras"}</span>
          <DeleteEntry onConfirm={remove} />
        </div>
      </div>
    </section>
  );
};
